import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Box, useTheme, Avatar, Menu as MenuMui, MenuItem, Modal, TextField, Grid2 as Grid } from '@mui/material';
import { Brightness4, Brightness7, Logout, Menu, Person, Clear as ClearIcon } from '@mui/icons-material';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme as useCustomTheme } from './contexts/ThemeContext';
import StyledComponents from './styles/StyledComponents';
import { colors, animations } from './styles/theme';

const { StickyHeader, GradientText, StyledButton, IconButton } = StyledComponents;

const HeaderContent = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const LogoSection = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  
  img {
    height: 45px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const NavSection = styled(Box)`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const RoleBadge = styled(motion.div)`
  background: ${colors.primary.gradient};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(212, 160, 23, 0.3);
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 70px;
  right: 0;
  background: ${({ theme }) => theme.mode === 'dark' ? colors.background.darkPaper : colors.background.paper};
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 200px;
`;

function Header({ role }) {
  const { isDarkMode, toggleTheme } = useCustomTheme();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileFormData, setProfileFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    console.log('User data in localStorage:', {
      name: localStorage.getItem('user_name'),
      email: localStorage.getItem('user_email'),
      role: localStorage.getItem('user_role'),
      id: localStorage.getItem('user_id')
    });
    
    // Initialize profile form with existing data
    const userName = localStorage.getItem('user_name') || '';
    const userEmail = localStorage.getItem('user_email') || localStorage.getItem('userEmail') || '';
    const names = userName.split(' ');
    
    setProfileFormData({
      firstName: names[0] || '',
      lastName: names.slice(1).join(' ') || '',
      email: userEmail,
      phone: localStorage.getItem('user_phone') || ''
    });
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const handleLogoClick = () => {
    if (!role) { navigate('/'); return; }
    switch (role) {
      case 'SuperAdmin':
      case 'Admin':
        navigate('/staff/admin-dashboard'); break;
      case 'Sales':
        navigate('/staff/sales-dashboard'); break;
      case 'Operations':
        navigate('/staff/operations-dashboard'); break;
      case 'ProjectManager':
        navigate('/staff/project-manager-dashboard'); break;
      case 'Superintendent':
        navigate('/staff/superintendent-dashboard'); break;
      case 'Vendor':
        navigate('/vendors/dashboard'); break;
      case 'Customer':
        navigate('/customer/selection'); break;
      default:
        navigate('/');
    }
  };

  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    
    try {
      const fullName = `${profileFormData.firstName} ${profileFormData.lastName}`.trim();
      
      // Update localStorage
      localStorage.setItem('user_name', fullName);
      localStorage.setItem('user_email', profileFormData.email);
      if (profileFormData.phone) {
        localStorage.setItem('user_phone', profileFormData.phone);
      }
      
      console.log('Profile updated successfully');
      setShowProfileModal(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const getUserInitials = () => {
    // Try multiple sources for user info
    const userName = localStorage.getItem('user_name');
    const userEmail = localStorage.getItem('user_email') || localStorage.getItem('userEmail');
    const userRole = localStorage.getItem('user_role');
    
    // If we have a name, use it
    if (userName && userName.trim()) {
      const names = userName.trim().split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return userName[0].toUpperCase();
    }
    
    // If we have an email, use first letter
    if (userEmail && userEmail.trim()) {
      return userEmail[0].toUpperCase();
    }
    
    // If we have a role, use first letter
    if (userRole) {
      return userRole[0].toUpperCase();
    }
    
    // Default fallback
    return 'U'; // for "User" instead of "?"
  };

  return (
    <StickyHeader 
      $glass 
      $scrolled={scrolled}
      style={{
        backdropFilter: scrolled ? 'blur(20px)' : 'blur(10px)',
        background: scrolled 
          ? (isDarkMode ? 'rgba(18, 18, 18, 0.8)' : 'rgba(255, 255, 255, 0.8)')
          : (isDarkMode ? 'rgba(18, 18, 18, 0.6)' : 'rgba(255, 255, 255, 0.6)'),
      }}
    >
      <HeaderContent>
        <LogoSection
          onClick={handleLogoClick}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <motion.img 
            src="/logo.png" 
            alt="Hallmark Homes Logo"
            initial={{ rotate: -180, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            <GradientText $gradient={colors.primary.gradient}>
              Hallmark Homes
            </GradientText>
          </Typography>
        </LogoSection>

        <NavSection>
          {role && (
            <>
              <RoleBadge
                {...animations.slideLeft}
                whileHover={{ scale: 1.05 }}
              >
                {role}
              </RoleBadge>
              
              <IconButton
                onClick={toggleTheme}
                variant="ghost"
                whileHover={{ rotate: 180 }}
                transition={{ duration: 0.3 }}
              >
                {isDarkMode ? <Brightness7 /> : <Brightness4 />}
              </IconButton>

              {/* Desktop Profile Avatar */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Avatar
                  onClick={handleProfileClick}
                  sx={{
                    bgcolor: colors.primary.main,
                    cursor: 'pointer',
                    width: 40,
                    height: 40,
                    fontSize: '1rem',
                    fontWeight: 600,
                    '&:hover': {
                      transform: 'scale(1.05)',
                      transition: 'transform 0.2s ease'
                    }
                  }}
                >
                  {getUserInitials()}
                </Avatar>
                <MenuMui
                  anchorEl={profileAnchorEl}
                  open={Boolean(profileAnchorEl)}
                  onClose={handleProfileClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  sx={{ mt: 1 }}
                >
                  <MenuItem onClick={() => {
                    handleProfileClose();
                    setShowProfileModal(true);
                  }} sx={{ py: 1, px: 2 }}>
                    <Person fontSize="small" sx={{ mr: 1 }} />
                    Profile Settings
                  </MenuItem>
                  <MenuItem onClick={() => { handleProfileClose(); handleLogout(); }} sx={{ py: 1, px: 2 }}>
                    <Logout fontSize="small" sx={{ mr: 1 }} />
                    Logout
                  </MenuItem>
                </MenuMui>
              </Box>

              {/* Mobile Menu Button */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <IconButton
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  variant="ghost"
                >
                  <Menu />
                </IconButton>
              </Box>
            </>
          )}
          
          {!role && (
            <IconButton
              onClick={toggleTheme}
              variant="ghost"
              whileHover={{ rotate: 180 }}
              transition={{ duration: 0.3 }}
            >
              {isDarkMode ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
          )}
        </NavSection>
      </HeaderContent>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && role && (
          <MobileMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Logged in as
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {role}
              </Typography>
            </Box>
            <StyledButton
              variant="outlined"
              size="small"
              fullWidth
              onClick={handleLogout}
            >
              <Logout fontSize="small" />
              Logout
            </StyledButton>
          </MobileMenu>
        )}
      </AnimatePresence>

      {/* Profile Settings Modal */}
      {showProfileModal && (
        <Modal open={showProfileModal} onClose={() => setShowProfileModal(false)}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
          }}>
            <Box sx={{
              bgcolor: '#d4a017',
              color: 'white',
              p: 3,
              borderRadius: '8px 8px 0 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Typography variant="h5" component="h2">
                Profile Settings
              </Typography>
              <IconButton 
                onClick={() => setShowProfileModal(false)} 
                sx={{ color: 'white', p: 0.5 }}
                disabled={profileLoading}
              >
                <ClearIcon />
              </IconButton>
            </Box>
            
            <Box component="form" onSubmit={handleProfileSubmit} sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={profileFormData.firstName}
                    onChange={(e) => setProfileFormData({ ...profileFormData, firstName: e.target.value })}
                    required
                    disabled={profileLoading}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={profileFormData.lastName}
                    onChange={(e) => setProfileFormData({ ...profileFormData, lastName: e.target.value })}
                    required
                    disabled={profileLoading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={profileFormData.email}
                    onChange={(e) => setProfileFormData({ ...profileFormData, email: e.target.value })}
                    required
                    disabled={profileLoading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone (Optional)"
                    value={profileFormData.phone}
                    onChange={(e) => setProfileFormData({ ...profileFormData, phone: e.target.value })}
                    disabled={profileLoading}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <StyledButton
                  type="button"
                  variant="outlined"
                  onClick={() => setShowProfileModal(false)}
                  disabled={profileLoading}
                >
                  Cancel
                </StyledButton>
                <StyledButton
                  type="submit"
                  variant="contained"
                  disabled={profileLoading}
                >
                  {profileLoading ? 'Updating...' : 'Update Profile'}
                </StyledButton>
              </Box>
            </Box>
          </Box>
        </Modal>
      )}
    </StickyHeader>
  );
}

export default Header;